<template>
  <div
    :class="[
      'workout-timer text-center',
      {
        '--x-small': xSmall,
        'w-100': !xSmall
      }
    ]"
    data-cy="workout-timer"
  >
    <slot name="before" />
    <div
      :class="[
        {
          '--running': isTimerStatus(TIMER_STATUS.RUNNING),
          '--paused': isTimerStatus(TIMER_STATUS.PAUSED),
          '--finished': isTimerStatus(TIMER_STATUS.FINISHED)
        },
        'workout-timer__element cta-row__element'
      ]"
    >
      <template v-if="!isCountingIn && !xSmall">
        <v-button
          class="workout-timer__change-button text-grey-70 mr-auto"
          small
          ghost
          @click="() => changeInitial(-1)"
          >-30</v-button
        >
        <v-button
          class="px-1 text-grey-70"
          small
          ghost
          data-cy="workout-mute"
          @click="toggleMuted(!areWorkoutsMuted)"
        >
          <v-icon :icon="areWorkoutsMuted ? 'volume_off' : 'volume_up'" />
        </v-button>
      </template>

      <div class="workout-timer__time text-center mr-1">
        <div
          v-if="isCountingIn ? !hideCountDown : true"
          data-cy="current-timer"
          class="workout-timer__time-text text-mono-numbers"
        >
          {{ timer.humanTime }}
        </div>
      </div>
      <template v-if="!isCountingIn">
        <v-button
          v-if="!hidePauseControl"
          class="workout-timer__toggle-button ml-1"
          small
          round
          light
          @click="toggleTimer"
        >
          <template v-if="isTimerStatus(TIMER_STATUS.RUNNING)">
            <v-icon icon="pause" class="workout-timer__icon" />
          </template>
          <template v-else>
            <v-icon icon="play_arrow" class="workout-timer__icon" />
          </template>
        </v-button>
        <v-button
          v-if="!xSmall"
          class="workout-timer__change-button text-grey-70 ml-auto"
          small
          ghost
          @click="() => changeInitial(+1)"
          >+30</v-button
        >
      </template>
    </div>
  </div>
</template>

<script>
import { TIMER_STATUS } from "@/lib/timer"
import { mapGetters, mapMutations } from "vuex"

export default {
  props: {
    timer: { type: Object, required: true },
    xSmall: { type: Boolean, default: false },
    hidePauseControl: { type: Boolean, default: false },
    hideCountDown: { type: Boolean, default: false }
  },

  data() {
    return {
      TIMER_STATUS
    }
  },

  beforeDestroy() {
    this.timer.reset()
  },

  methods: {
    ...mapMutations("workouts", ["setWorkoutsMuted"]),

    toggleTimer() {
      if (this.initializedOrFinished) {
        this.timer.reset()
        this.timer.start()
      } else if (this.isTimerStatus(TIMER_STATUS.PAUSED)) {
        this.timer.start()
      } else {
        this.timer.pause()
      }
    },

    isTimerStatus(status) {
      return !!this.timer && this.timer.status === status
    },

    changeInitial(forward = 1) {
      const seconds = this.timer.getSeconds()
      this.timer.updateSeconds(seconds + 30 * forward)
    },

    toggleMuted(isMuted) {
      this.timer.mute(isMuted)
      this.setWorkoutsMuted(isMuted)
    }
  },

  computed: {
    ...mapGetters("workouts", ["areWorkoutsMuted"]),

    isCountingIn() {
      return this.isTimerStatus(TIMER_STATUS.COUNTING_IN)
    },

    initializedOrFinished() {
      return (
        this.isTimerStatus(TIMER_STATUS.INITIALIZED) ||
        this.isTimerStatus(TIMER_STATUS.FINISHED)
      )
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.workout-timer {
  // animation: fade-in-up 0.4s ease;
  font-family: $system-font-stack;

  color: $color-ui--grey-80;
  position: relative;
  overflow: hidden;

  &.--x-small {
    padding: $size--2 $size--3;
    min-height: 40px;

    .workout-timer__time-text {
      font-size: 1rem;
      min-height: 0;
      line-height: 1;
    }

    .workout-timer__icon {
      font-size: $size--3;
      height: $size--3;
    }

    .workout-timer__toggle-button {
      height: 1.5rem !important;
      min-height: 1.5rem !important;
      max-height: 1.5rem !important;
      width: 1.5rem !important;
      min-width: 1.5rem !important;
      max-width: 1.5rem !important;
      padding: 0 !important;
    }

    .workout-timer__element {
      padding: 0 !important;
    }
  }
}

.workout-timer__volume {
  height: 2px;
  background-color: $color-ui--grey-50;
}

.workout-timer__time-text {
  // font-size: $font-size--h1 + $font-size--display-3;
  font-size: 50px;
  line-height: $size--9;
  font-weight: $font-weight--x-bold;
  color: $color-ui--grey-90;
  transform-origin: 0% 100%;
  transition: transform 0.2s ease, color 0.3s ease;
  min-height: 44px;
  display: flex;
  letter-spacing: -1px;
  border-radius: $size--2;
}

.workout-timer__icon {
  font-size: $size--5;
  height: $size--5;
}

.workout-timer__toggle-button {
  height: 2.5rem !important;
  min-height: 2.5rem !important;
  max-height: 2.5rem !important;
  width: 2.5rem !important;
  min-width: 2.5rem !important;
  max-width: 2.5rem !important;
}

.workout-timer__change-button {
  transition: none;
  padding-left: $size--1 !important;
  padding-right: $size--1 !important;
  overflow: visible;
  text-overflow: unset;
}

.workout-timer__element {
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  padding: 0;
  padding-left: $size--3 !important;
  padding-right: $size--3 !important;
  padding-top: $size--6 !important;
  padding-bottom: $size--6 !important;

  &.--running,
  &.--counting-in {
    color: var(--brand-color-hex);
    border-color: var(--brand-color-hex);

    h1 {
      color: var(--brand-color-hex);
    }
  }

  &.--paused {
    color: $color-ui--grey-60;
    border-color: $color-ui--grey-60;
  }

  &.--finished {
    color: $color-ui--grey-40;
    border-color: $color-ui--grey-40;
  }
}
</style>
